import { Link } from "react-router-dom";
import { IoSearch } from "react-icons/io5";
import Cart from "svgs/cart";
import logo from "../../src/images/safiyaLogo.webp";
import { useEffect, useMemo, useState } from "react";
import useAllProductsData from "hook/useAllProductsData";
import Lottie from "lottie-react";
import flameAnimation from "../json/FlameAnimation.json";
import SettingsPanel from "components/SettingsPanel";
import { useSettings } from "context/SettingsContext";
import { t } from "context/translations";
import { Settings } from "lucide-react";
import { assetUrl } from "utils/api";
import {
  CART_STORAGE_KEY,
  CART_UPDATED_EVENT,
  getCartItemCount,
} from "utils/cartStorage";

const normalizeText = (text = "") =>
  text.toString().toLowerCase().replace(/\s+/g, "").trim();

const Navbar = () => {
  const [searchText, setSearchText] = useState("");
  const { allProducts } = useAllProductsData();
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [cartItemCount, setCartItemCount] = useState(0);
  const { theme, language } = useSettings();
  const isDark = theme === "dark";

  useEffect(() => {
    const updateCartItemCount = () => {
      setCartItemCount(getCartItemCount());
    };

    const handleStorage = (event) => {
      if (event.key === CART_STORAGE_KEY) {
        updateCartItemCount();
      }
    };

    updateCartItemCount();
    window.addEventListener(CART_UPDATED_EVENT, updateCartItemCount);
    window.addEventListener("storage", handleStorage);

    return () => {
      window.removeEventListener(CART_UPDATED_EVENT, updateCartItemCount);
      window.removeEventListener("storage", handleStorage);
    };
  }, []);


  const variants = useMemo(() => {
    return allProducts.flatMap((product) =>
      product.variants.map((variant) => ({
        variant_id: variant.variant_id,
        title: variant.title,
        title_normalized: normalizeText(variant.title),
        flashPrice: variant.flashPrice,
        item_id: product.item_id,
        catalogue: product.catalogue,
        image: variant.images?.[0] ?? null,
      })),
    );
  }, [allProducts]);

  const searchResults = useMemo(() => {
    if (!searchText.trim()) return [];
    const keyword = normalizeText(searchText);
    return variants
      .filter((v) => v.title_normalized.includes(keyword))
      .slice(0, 10);
  }, [searchText, variants]);

  return (
    <>
      <nav className="bg-white sticky top-0 z-50 border-b-2">
        <div className="relative md:max-w-7xl mx-auto px-4 sm:px-4 2xl:px-0 lg:px-8">
          {/* <div className="relative rounded-md md:max-w-7xl mx-auto select-none"> */}
          <div className="flex items-center justify-between py-2">
            {/* Mobile left */}
            <div className="flex items-center justify-between gap-2 md:hidden w-full">
              <div className="flex items-center gap-4 shrink-0">
                {/* <button onClick={toggleDrawer}>
                  <FiMenu size={22} className="text-white" />
                </button> */}
                <Link to="/">
                  <img src={logo} className="h-10" alt="logo" />
                </Link>
              </div>
              <div className="relative w-full max-w-[55%]">
                <input
                  lang="bn"
                  type="text"
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                  placeholder={t("searchPlaceholder", language)}
                  className={`w-full border rounded-full px-3 py-1.5 text-sm ${isDark ? "bg-gray-800 border-gray-600 text-white placeholder-gray-400" : "border-gray-300"}`}
                />

                <button className="absolute right-0 top-0 h-full px-2  text-gray-400 rounded-r-md">
                  <IoSearch />
                </button>

                {/* Mobile Suggestions */}
                {searchText && (
                  <div
                    className={`absolute z-50 border w-full mt-1 rounded shadow max-h-72 overflow-y-auto ${isDark ? "bg-gray-800 border-gray-600" : "bg-white"}`}
                  >
                    {searchResults.length > 0 ? (
                      searchResults.map((item) => (
                        <Link
                          key={item.variant_id}
                          to={`/details/${item.catalogue}/${item.item_id}`}
                          onClick={() => setSearchText("")}
                          className={`flex items-center gap-2 p-2 ${isDark ? "hover:bg-gray-700" : "hover:bg-gray-100"}`}
                        >
                          <img
                            src={assetUrl(item.image)}
                            className="w-8 h-8 rounded object-cover"
                            alt={item.title}
                          />
                          <div>
                            <p className="text-xs font-medium">{item.title}</p>
                            <p className="text-[10px] text-gray-500">
                              ৳ {item.flashPrice}
                            </p>
                          </div>
                        </Link>
                      ))
                    ) : (
                      <div className="p-3 text-center text-xs text-gray-500">
                        {t("noProductFound", language)}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Desktop logo */}
            <div className="hidden lg:flex items-center w-full py-1">
              <div className="flex basis-1/5 items-center mr-5">
                <span className="text-lg font-bold text-backgroundColor">
                  <Link to="/" className="flex items-center">
                    {/* <p>Safiya Enterprise</p> */}
                    <img
                      src={logo}
                      alt="Safiya Enterprise Logo"
                      className="h-12 w-auto"
                    />
                  </Link>
                </span>
              </div>
              <div className="hidden md:flex md:basis-3/5 items-center w-full gap-6">
                {/* 🔍 Search Box */}
                <div className="relative flex-grow">
                  <input
                    lang="bn"
                    type="text"
                    value={searchText}
                    onChange={(e) => setSearchText(e.target.value)}
                    placeholder={t("searchPlaceholder", language)}
                    className={`w-full border rounded-full px-4 py-2 placeholder-gray-400 ${isDark ? "bg-gray-800 border-gray-600 text-white" : "bg-gray-50 border-gray-300"}`}
                  />

                  <button className="absolute right-0 top-0 h-full px-4  text-white rounded-r-md">
                    <IoSearch />
                  </button>

                  {/* Suggestions */}
                  {searchText && (
                    <div
                      className={`absolute z-50 border w-full mt-1 rounded shadow max-h-96 overflow-y-auto ${isDark ? "bg-gray-800 border-gray-600" : "bg-white"}`}
                    >
                      {searchResults.length > 0 ? (
                        searchResults.map((item) => (
                          <Link
                            key={item.variant_id}
                            to={`/details/${item.catalogue}/${item.item_id}`}
                            onClick={() => setSearchText("")}
                            className={`flex items-center gap-3 p-2 ${isDark ? "hover:bg-gray-700" : "hover:bg-gray-100"}`}
                          >
                            <img
                              src={assetUrl(item.image)}
                              className="w-10 h-10 rounded object-cover"
                              alt={item.title}
                            />
                            <div>
                              <p
                                className={`text-sm font-medium ${isDark ? "text-white" : ""}`}
                              >
                                {item.title}
                              </p>
                              <p className="text-xs text-gray-500">
                                ৳ {item.flashPrice}
                              </p>
                            </div>
                          </Link>
                        ))
                      ) : (
                        <div className="p-4 text-center text-sm text-gray-500">
                          {t("noProductFound", language)}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
              <div className="flex basis-1/5 justify-end gap-4">
                <div className="hidden md:flex items-center gap-4 ml-5">
                  <Link
                    to="/cart"
                    className={`relative rounded-md p-1 ${isDark ? "text-white" : "text-black"}`}
                  >
                    <Cart />
                    {cartItemCount > 0 && (
                      <span className="absolute -right-2 -top-2 min-w-5 h-5 px-1 rounded-full bg-red-600 text-white text-[11px] font-bold leading-5 text-center">
                        {cartItemCount > 99 ? "99+" : cartItemCount}
                      </span>
                    )}
                    <span className="sr-only">Cart</span>
                  </Link>
                </div>
                <div
                  className=" w-10 h-10 rounded-md bg-red-300  overflow-hidden flex items-center justify-center"
                  lang="bn"
                >
                  <Link to="/hotOffer">
                    <Lottie
                      animationData={flameAnimation}
                      loop={true}
                      style={{ transform: "scale(1.2)" }}
                      className="w-full h-full"
                    />
                  </Link>
                </div>
                <button
                  onClick={() => setIsSettingsOpen(true)}
                  aria-label="Open settings"
                  className={`p-2 rounded-full transition ${isDark ? "text-white hover:bg-gray-700" : "text-gray-700 hover:bg-gray-100"}`}
                >
                  <Settings className="w-5 h-5" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </nav>


      <SettingsPanel
        isOpen={isSettingsOpen}
        onClose={() => setIsSettingsOpen(false)}
      />
    </>
  );
};

export default Navbar;
